import { bootstrapApplication } from '@angular/platform-browser';

import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

if (environment.production) {
  console.warn = () => {
  };
}

/**
 * Pre-bootstrap Service Worker recovery configuration.
 * Uses localStorage with a 5-minute TTL to prevent rapid reload spirals
 * across lazy-loaded chunk failures and flaky network conditions.
 */
const SW_RECOVERY_KEY = 'sw-recovery-attempt';
const RECOVERY_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface RecoveryFlag {
  expiry: number;
}

/**
 * Checks if a recovery attempt was made recently within the TTL window.
 */
function isRecoveryLocked(): boolean {
  try {
    const itemStr = localStorage.getItem(SW_RECOVERY_KEY);
    if (!itemStr) return false;

    const flag: RecoveryFlag = JSON.parse(itemStr);
    if (Date.now() > flag.expiry) {
      localStorage.removeItem(SW_RECOVERY_KEY);
      return false;
    }
    return true;
  } catch {
    // Fallback for private browsing or disabled storage
    return false;
  }
}

function clearServiceWorkerAndReload(): void {
  try {
    const flag: RecoveryFlag = {expiry: Date.now() + RECOVERY_TTL_MS};
    localStorage.setItem(SW_RECOVERY_KEY, JSON.stringify(flag));
  } catch {
  }

  const tasks: Promise<unknown>[] = [];

  if ('serviceWorker' in navigator) {
    tasks.push(
      navigator.serviceWorker.getRegistrations()
        .then(registrations => Promise.all(registrations.map(r => r.unregister())))
    );
  }

  if ('caches' in window) {
    tasks.push(
      caches.keys().then(names => Promise.all(names.map(n => caches.delete(n))))
    );
  }

  Promise.all(tasks).finally(() => location.reload());
}

// Read the lock state immediately on startup
const recoveryAttemptedRecently = isRecoveryLocked();

// Set a timeout to detect if the app fails to bootstrap in a reasonable time.
// If we recently tried a recovery, we skip this timeout to prevent infinite reload spirals.
const bootstrapTimeout = !recoveryAttemptedRecently
  ? setTimeout(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        if (registrations.length > 0) {
          console.error('App bootstrap timeout — clearing stale Service Worker caches');
          clearServiceWorkerAndReload();
        }
      });
    }
  }, 15000)
  : null;

bootstrapApplication(AppComponent, appConfig)
  .then(() => {
    if (bootstrapTimeout) {
      clearTimeout(bootstrapTimeout);
    }
    document.getElementById('initial-loader')?.remove();
  })
  .catch((err) => {
    if (bootstrapTimeout) {
      clearTimeout(bootstrapTimeout);
    }
    console.error('Bootstrap failed:', err);

    if (!recoveryAttemptedRecently && 'serviceWorker' in navigator) {
      clearServiceWorkerAndReload();
    }
  });
